import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { usePlanLimit } from "../../../../hooks/usePlanLimit";
import { motion, AnimatePresence } from "framer-motion";
import {
    useGetRolesQuery,
    useGetCompaniesQuery,
    useCreateStaffInvitationMutation,
    useUpdateInviteMutation,
    useResendInviteMutation,
    useUpdateStaffRoleMutation,
} from "../../../../redux/services/staffApi";
import { useGetAllBranchesQuery } from "../../../../redux/services/inviteApi";
import { Staff } from "../../../Table/StaffTable";
import InfoTooltip from "../../../Tooltip/InfoTooltip";

type Role = { id: string; name: string };
type Branch = { id: string; branch_detail?: { branch_name: string } };
type Company = { id: string; company_name: string };

type FormState = {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    gender: string;
    roleId: string;
    resumptionDate: string;
    branchId: string;
    companyId: string;
};

type Props = {
    onBack: () => void;
    action?: "add" | "update" | "resend" | "updatestaff";
    staff?: Staff | null;
};

const Dropdown = ({
    label,
    value,
    onChange,
    options,
    placeholder,
    disabled,
    loading,
    required,
    tooltip,
}: {
    label: string;
    value: string;
    onChange: (value: string) => void;
    options: { label: string; value: string }[];
    placeholder: string;
    disabled?: boolean;
    loading?: boolean;
    required?: boolean;
    tooltip?: string;
}) => {
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const selected = options.find((opt) => opt.value === value);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div ref={dropdownRef} className="relative">
            <label className="text-sm font-medium text-slate-600 mb-1 flex items-center gap-1.5">
                {label} {required && <span className="text-red-500">*</span>}
                {tooltip && <InfoTooltip position="right" text={tooltip} />}
            </label>
            <div
                onClick={() => !disabled && setOpen(!open)}
                className={`flex justify-between items-center w-full border rounded-lg px-3 py-2 cursor-pointer transition ${disabled
                    ? "bg-gray-100 cursor-not-allowed"
                    : "hover:border-blue-500 focus:ring-2 focus:ring-blue-500"
                    }`}
            >
                <span className={`text-sm ${!value ? "text-slate-400" : "text-slate-700"} truncate`}>
                    {loading ? `Loading ${label.toLowerCase()}...` : selected ? selected.label : placeholder}
                </span>
                <svg
                    className={`w-4 h-4 text-gray-500 transition-transform ${open ? "rotate-180" : ""}`}
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </div>

            <AnimatePresence>
                {open && !disabled && !loading && (
                    <motion.div
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -5 }}
                        transition={{ duration: 0.15 }}
                        className="absolute z-10 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto"
                    >
                        {options.length > 0 ? (
                            options.map((opt) => (
                                <div
                                    key={opt.value}
                                    onClick={() => {
                                        onChange(opt.value);
                                        setOpen(false);
                                    }}
                                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 ${opt.value === value ? "bg-blue-100" : ""
                                        }`}
                                >
                                    {opt.label}
                                </div>
                            ))
                        ) : (
                            <div className="px-3 py-2 text-sm text-slate-400">No options available</div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

const StaffManualAdd: React.FC<Props> = ({ onBack, action = "add", staff = null }) => {    
    const [form, setForm] = useState<FormState>({
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        gender: "",
        roleId: "",
        resumptionDate: "",
        branchId: "",
        companyId: "",
    });

    const isUpdateStaffView = action === "updatestaff";
    const isResend = action === "resend"; 

    const { data: rolesData = [], isLoading: rolesLoading } = useGetRolesQuery();
    const { data: branchesData = [], isLoading: branchesLoading } = useGetAllBranchesQuery();
    const { data: companyData, isLoading: companyLoading } = useGetCompaniesQuery();
    const companyInfo = companyData?.data?.companyInfo;

    const [createInvite, { isLoading: creating }] = useCreateStaffInvitationMutation();
    const { handlePlanLimitError } = usePlanLimit();
    const [updateStaff, { isLoading: updating }] = useUpdateInviteMutation();
    const [resendInvite, { isLoading: resending }] = useResendInviteMutation();
    const [updateStaffRole, { isLoading: updatingStaffRole }] = useUpdateStaffRoleMutation();

    useEffect(() => {
        if (staff && (action === "update" || action === "resend" || action === "updatestaff")) {
            const [firstName = "", lastName = ""] = staff.name?.split(" ") || ["", ""];

            setForm({
                firstName,
                lastName,
                email: staff.email || "",
                phoneNumber: staff.phone_number || "",
                gender: staff.gender || "",
                roleId: staff.role ?? "",
                resumptionDate: staff.dateOfResumption
                    ? staff.dateOfResumption.split(" ")[0]
                    : "",
                branchId: staff.branch_id || "",
                companyId: staff.company_id || "",
            });
        }
    }, [staff, action]);

    // rolename --role ID
    useEffect(() => {
        if (!staff || !rolesData.length) return;

        const matchedRole = rolesData.find(
            (role: Role) => role.name === staff.role
        );

        if (matchedRole) {
            setForm((prev) => ({
                ...prev,
                roleId: matchedRole.id,
            }));
        }
    }, [rolesData, staff]);

    // company
    useEffect(() => {
        if (!companyInfo?.id) return;

        setForm((prev) => ({
            ...prev,
            companyId: companyInfo.id,
        }));
    }, [companyInfo]);

    const handleChange = (field: keyof FormState, value: string) =>
        setForm((prev) => ({ ...prev, [field]: value }));

    const extractErrorMessage = (error: any): string => {
        const data = error?.data;
        if (data?.errors && typeof data.errors === "object") {
            const firstField = Object.keys(data.errors)[0];
            const firstMsg = data.errors[firstField]?.[0];
            if (firstMsg) return firstMsg;
        }
        const msg = data?.message || data?.error?.message || data?.error;
        if (typeof msg === "string") return msg;
        return "Failed to process request. Please try again.";
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Front-end validation for required fields
        if (!isResend) {
            if (!isUpdateStaffView && !form.email.trim()) {
                toast.error("Email address is required");
                return;
            }
            if (!form.roleId) {
                toast.error("Role is required");
                return;
            }
        }

        try {
            if (isResend && staff?.id) {
                await resendInvite(staff.id).unwrap();
                toast.success("Invitation resent successfully!");
            } else if (action === "update" && staff?.id) {
                await updateStaff({
                    id: staff.id,
                    first_name: form.firstName,
                    last_name: form.lastName,
                    phone_number: form.phoneNumber,
                    gender: form.gender,
                    role_id: form.roleId,
                    resumption_date: form.resumptionDate,
                    branch_id: form.branchId,
                    company_id: form.companyId,
                }).unwrap();
                toast.success("Staff updated successfully!");
            } else if (isUpdateStaffView && staff?.id) {
                await updateStaffRole({ staffId: staff.id, role_id: form.roleId }).unwrap();
                toast.success("Staff updated successfully!");
            } else {
                await createInvite({
                    first_name: form.firstName,
                    last_name: form.lastName,
                    email: form.email,
                    phone_number: form.phoneNumber,
                    gender: form.gender,
                    role_id: form.roleId,
                    resumption_date: form.resumptionDate,
                    branch_id: form.branchId,
                    company_id: form.companyId,
                }).unwrap();
                toast.success("Invitation sent successfully!");
            }
            onBack();
        } catch (error: any) {
            if (handlePlanLimitError(error)) return;
            toast.error(extractErrorMessage(error));
        }
    };

    const title = isResend
        ? "Resend Staff Invitation"
        : action === "update"
            ? "Update Staff Invitation"
            : isUpdateStaffView
                ? "Update Staff Role"
                : "Add Staff Manually";

    const buttonLabel = isResend
        ? "Resend Invite"
        : action === "update"
            ? "Update Staff Invitation"
            : isUpdateStaffView
                ? "Update Staff"
                : "Add Staff";

    return (
        <div className="bg-white rounded-xl shadow p-6">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-[#143887]">{title}</h2>
                <button type="button" onClick={onBack} className="text-sm text-gray-500 hover:underline">
                    ← Back
                </button>
            </div>

            <p className="text-sm text-slate-500 mb-4">
                {isResend
                    ? "Resend invitation with the same staff details"
                    : action === "update"
                        ? "Update staff information below"
                        : "Grow your team by adding one staff at a time"}
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">

                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {/* All fields prefilled, non-editable if resending */}
                    <div>
                        <label className="block text-sm font-medium text-slate-600 mb-1">First Name</label>
                        <input
                            type="text"
                            value={form.firstName}
                                onChange={(e) => handleChange("firstName", e.target.value)} 
                            disabled={isResend || isUpdateStaffView}
                            className={`w-full border rounded-lg px-3 py-2 ${isResend || isUpdateStaffView
                                ? "bg-gray-100 cursor-not-allowed"
                                : "focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                }`}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-600 mb-1">Last Name</label>
                        <input
                            type="text"
                            value={form.lastName}
                            onChange={(e) => handleChange("lastName", e.target.value)}
                            disabled={isResend || isUpdateStaffView}
                            className={`w-full border rounded-lg px-3 py-2 ${isResend || isUpdateStaffView
                                ? "bg-gray-100 cursor-not-allowed"
                                : "focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                }`}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-600 mb-1">
                            Email {!isResend && !isUpdateStaffView && <span className="text-red-500">*</span>}
                        </label>
                        <input
                            type="email"
                            value={form.email}
                            onChange={(e) => handleChange("email", e.target.value)}
                            disabled={isResend || action === "update" || isUpdateStaffView}
                            className={`w-full border rounded-lg px-3 py-2 ${isResend || action === "update" || isUpdateStaffView
                                ? "bg-gray-100 cursor-not-allowed"
                                : "focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                }`}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-slate-600 mb-1">Phone Number</label>
                        <input
                            type="text"
                            value={form.phoneNumber}
                            disabled={isResend || isUpdateStaffView}
                            onChange={(e) => handleChange("phoneNumber", e.target.value)}
                            className={`w-full border rounded-lg px-3 py-2 ${isResend || isUpdateStaffView
                                ? "bg-gray-100 cursor-not-allowed"
                                : "focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                }`}
                        />
                    </div>

                    <Dropdown
                        label="Gender"
                        value={form.gender}
                        onChange={(val) => handleChange("gender", val)}
                        options={[
                            { label: "Male", value: "male" },
                            { label: "Female", value: "female" },
                        ]}
                        placeholder="Select gender"
                        disabled={isResend || isUpdateStaffView}
                    />

                    <Dropdown
                        label="Role"
                        required={!isResend}
                        value={form.roleId}
                        onChange={(val) => handleChange("roleId", val)}
                        options={rolesData.map((role: Role) => ({ label: role.name, value: role.id })) || []}
                        placeholder="Select role"
                        loading={rolesLoading}
                        disabled={isResend}
                        tooltip="The role you select determines exactly which pages and actions this staff member can access across Fleetrify."
                    />

                    <Dropdown
                        label="Branch"
                        value={form.branchId}
                        onChange={(val) => handleChange("branchId", val)}
                        options={branchesData.map((branch: Branch) => ({
                            label: branch.branch_detail?.branch_name || "",
                            value: branch.id,
                        }))}
                        placeholder="Select branch"
                        loading={branchesLoading}
                        disabled={isResend || isUpdateStaffView}
                    />

                    {!isUpdateStaffView && (
                        <Dropdown
                            label="Company"
                            required={!isResend}
                            value={form.companyId}
                            onChange={(val) => handleChange("companyId", val)}
                            options={companyInfo ? [{ label: companyInfo.company_name || "", value: companyInfo.id }] : []}
                            placeholder="Select company"
                            loading={companyLoading}
                            disabled={isResend}
                        />
                    )}

                    {!isUpdateStaffView && (
                        <div>
                            <label className="text-sm font-medium text-slate-600 mb-1 flex items-center gap-1.5">
                                Resumption Date
                                <InfoTooltip
                                    position="right"
                                    text="The date this staff member is expected to start. This is recorded on their profile for reference and doesn't restrict their invite or login."
                                />
                            </label>
                            <input
                                type="date"
                                value={form.resumptionDate}
                                onChange={(e) => handleChange("resumptionDate", e.target.value)}
                                    min={new Date().toISOString().split("T")[0]}
                                disabled={isResend}
                                className={`w-full border rounded-lg px-3 py-2 ${isResend ? "bg-gray-100 cursor-not-allowed" : "focus:ring-2 focus:ring-blue-500 focus:outline-none"}`}
                            />
                        </div>
                    )}
                </div>           

                <div className="flex justify-end gap-3 pt-4 border-t">
                    <button
                        type="button"
                        onClick={onBack}
                        className="rounded-lg border px-4 py-2 text-sm hover:bg-slate-100"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={creating || updating || resending || updatingStaffRole}
                        className="rounded-lg bg-[#143887] text-white px-4 py-2 text-sm hover:bg-blue-700 transition disabled:opacity-70"
                    >
                        {creating || updating || resending || updatingStaffRole ? "Processing..." : buttonLabel}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default StaffManualAdd;





